/* eslint-disable react-hooks/refs --
   `viewRef`, `pendingRestoreRef` and `knownLayoutRef` are read and written during render on
   purpose, and the rule can't tell this case from the bug it guards. Neither
   is rendered: they hold what the NEXT grid should be put back to, for a
   callback that outlives the render which computed it, and only AG Grid ever
   reads them. See the `viewRef` comment below. */
// frontend/src/hooks/useGridState.js
//
// Everything a NEW grid instance starts with. Pages spread the whole return
// value onto <AgGridReact/>, so none of it can be wired up by half.
//
// DURABLE — per USER, backend `ui_preferences.grid_state`, survives logout:
//   column visibility and sort, and the column layout the merchant dragged —
//   order and widths (`columnLayout`, utils/merchantColumnLayout). What the
//   merchant configured. Every grid.
//
// VOLATILE — per TAB, sessionStorage, dies with the tab:
//   current page and expanded rows. Where the merchant was. Server-side row
//   model only — see capturedViewState for why the client-side one is excluded.
//
// A tab switch keeps the grid (tabs/tabPaneGridLifecycle.test.jsx), so the
// position only has to outlive the grid across F5 and a genuine rebuild. It
// is keyed per TAB because two Inventory tabs open on different pages stay on
// different pages, and it lives in sessionStorage so F5 restores too.
//
// A grid is put back together from two places, and they carry different things:
//
//   `initialState` — read once, by the FIRST grid a page builds. Everything a
//     grid should be born with, the page's default-hidden columns included.
//   the `gridInitializing` state event — every grid, including the rebuilt
//     ones `initialState` can no longer reach (onStateUpdated explains why).
//     The MERCHANT's state only: their columns, their sort, their position.
//
// The merchant's column order rides on both, as AG Grid's `columnOrder` state:
// in `initialState` AG Grid holds the columns hidden until it is applied
// (ag-grid-community `StateService.postConstruct`), so they never jump. The
// widths they dragged come back on the state event instead, for every grid
// (utils/merchantColumnLayout): AG Grid's `columnSizing` state would reset
// `flex` on every column it does not list.
//
// The defaults are absent from the second. They are a hand-written list of
// colIds, so they say nothing about the columns a columnDef hides, and
// re-applying one to a live grid reveals every column it omits. A rebuilt grid
// does not need them: they come from a page-level constant, so the `initialState`
// it reads holds the same defaults a fresh one would.
//
// `alwaysHidden` rides on BOTH: colIds the merchant can never reveal, merged
// into every hiddenColIds list this hook hands a grid. AG Grid applies a
// columnVisibility state as a complete picture — every column it does not list
// is shown — and a merchant's capture lists only the columns the grid had at
// the time. A column that exists in one mode of a page (the field a grouped
// grid groups by) is missing from a state saved in the other mode, so without
// this the restore would reveal it. A colId naming a column the grid does not
// have is ignored by AG Grid (an unmatched state entry), so the merge is safe
// in every mode. The merchant's capture leaves them out again: they are not a
// choice, and a page whose list changes with its mode (Customer orders hides
// its month column only while grouped) would otherwise carry a grouped-mode
// capture into the flat view and hide a column the merchant never hid.
//
// `movableColumns: false` is for a grid whose column order means something —
// months in time order. It hands the grid AG Grid's `suppressMovableColumns`,
// which stops a drag in the header and in the column picker, and it ignores a
// saved order: AG Grid applies a `columnOrder` state whatever that option says,
// so an order saved before the grid was fixed would put the months back out of
// sequence. The merchant's widths still restore.
//
// Deliberately NOT restored, each for its own reason:
//   - Widths the merchant did not drag — AG Grid sizes those columns
//     (utils/gridDefaults, "Column sizing").
//   - Column pinning — the developer's; a dragged column cannot be pinned
//     (`defaultColDef.lockPinned`).
//   - Row selection — a rebuilt grid re-fetches its rows from the server, so
//     a restored "select all" would re-apply to a row set that may have
//     changed in between.
//   - Scroll offset — not in AG Grid 36's state for the server-side row model.
//     The page is our unit of position, and every SSRM grid here paginates.
//   - Filter model — grids run with AG Grid filters off (`floatingFilter: false`,
//     no `filter` in defaultColDef); merchant filters are React state, persisted
//     by useTabPersistedState.
//
// Visibility and sort are colId-keyed. A stale hidden colId is ignored, so a
// renamed column shows. A stale sort term clears on the master-data grids:
// api/filters.py's SortAliasOrderingFilter answers it with a 400 that
// useSsrmGrid's invalid_sort handler turns into a cleared sort. The REPORT
// grids order through reporting/rowsource.py's build_order_by, which skips an
// unknown term and falls back to the report's default order, so the dead colId
// stays in ui_preferences and a report column rename needs no migration.

import { useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOptionalTabId } from '../tabs/tabsContexts';
import { loadTabSlot, saveTabSlot } from '../tabs/storage';
import { changedMerchantLayout, onMerchantLayoutReset, restoreMerchantLayout } from '../utils/merchantColumnLayout';

const DEBOUNCE_MS = 1000;

// The state keys AG Grid reports a column move or resize under — ours as well
// as the merchant's, so each one asks changedMerchantLayout and saves only a
// change.
const LAYOUT_SOURCES = ['columnOrder', 'columnSizing'];

// Namespace inside the tab's slot store, so grid view state can't collide with
// a page's filter slots (useTabPersistedState writes to the same tab keyspace).
const VIEW_SLOT_PREFIX = 'grid.';

// Where AG Grid records expanded rows, keyed by getRowId: row-group and
// master-detail expansion both land here (`updateGroupExpansionState` in
// ag-grid-community); `ssrmRowGroupExpansion` is used instead only under
// `ssrmExpandAllAffectsAllRows`, which no grid sets. The master-detail path is
// pinned end to end in useGridState.tabRestore.test.jsx.
const EXPANSION_KEYS = ['rowGroupExpansion'];

// AG Grid names the sources of a state change after the state keys themselves,
// so these are both the keys we read and the events we read them on.
const POSITION_SOURCES = ['pagination', ...EXPANSION_KEYS];

// Reduce a saved grid_state blob to what is restored: visibility and sort.
// Every other key is dropped, so it is never re-applied. An empty sort is
// dropped too: a header click never clears the sort (gridDefaults
// `sortingOrder`), and restored it would clear the column's `initialSort`.
// Returns null when nothing restorable remains.
export function restorableColumnState(saved) {
  if (!saved) return null;
  const { columnVisibility, sort } = saved;
  const rest = {};
  if (columnVisibility) rest.columnVisibility = columnVisibility;
  if (sort?.sortModel?.length) rest.sort = sort;
  return Object.keys(rest).length === 0 ? null : rest;
}

// The slice of an AG Grid state that says WHERE THE MERCHANT IS. Returns null
// when there is nothing worth keeping, which is also how a merchant who paged
// back to the first page clears a stale slot: the caller stores this verbatim.
//
// Position is kept for the SERVER-SIDE row model only. It is re-applied the
// moment a rebuilt grid announces itself, and at that moment a client-side
// grid has no rows: its `rowData` prop arrives just after, so "go to page 5"
// clamps to page 1. Only the server-side model can assume a row count up front
// (see viewRestoreProps). Column preferences restore on both models.
export function capturedViewState(state, rowModelType) {
  if (rowModelType !== 'serverSide') return null;
  const view = {};
  for (const key of EXPANSION_KEYS) {
    const expansion = state?.[key];
    // An empty expandedRowGroupIds is the default state, not a position.
    if (expansion?.expandedRowGroupIds?.length) view[key] = expansion;
  }
  // Page 0 is where a grid starts; storing it would be storing "no position".
  const { page, pageSize } = state?.pagination ?? {};
  if (page > 0) view.pagination = { page, pageSize };
  if (Object.keys(view).length === 0) return null;
  return view;
}

// Turn a stored view into the two props that put a new grid back where the
// merchant left it: a fragment of `initialState`, and the row count the
// server-side row model needs before it will accept a starting page.
export function viewRestoreProps(view) {
  if (!view) return { state: null, serverSideInitialRowCount: undefined };

  const state = {};
  for (const key of EXPANSION_KEYS) {
    if (view[key]) state[key] = view[key];
  }

  let serverSideInitialRowCount;
  const { page, pageSize } = view.pagination ?? {};
  if (page > 0) {
    // Page only, never pageSize: the page's own paginationPageSize prop is the
    // one source of truth for that (it must also match cacheBlockSize), and a
    // stored pageSize would silently override it after a developer changes the
    // constant.
    state.pagination = { page };
    // The server-side model will not move to a page it has no rows for, and it
    // has no row count until a block comes back. This count only has to COVER
    // the restored page; the first block's response carries the real one.
    serverSideInitialRowCount = (page + 1) * pageSize;
  }

  return {
    state: Object.keys(state).length === 0 ? null : state,
    serverSideInitialRowCount,
  };
}

const NO_COL_IDS = [];

// The merchant's saved order as AG Grid `columnOrder` state on top of `state`.
// AG Grid applies such a state to the columns it lists and drops every other
// entry of the state for a column it does not (ag-grid-community
// `applyColumnGridState`), so a hidden or sorted column missing from the saved
// order — one added since, or the field of the page's other mode — would show
// or lose its sort. Each rides at the end of the order instead; a colId the
// grid does not have is ignored.
export function withColumnOrder(state, order) {
  if (!order) return state;
  const orderedColIds = [...new Set([
    ...order,
    ...(state?.columnVisibility?.hiddenColIds ?? []),
    ...(state?.sort?.sortModel ?? []).map(({ colId }) => colId),
  ])];
  return { ...state, columnOrder: { orderedColIds }, partialColumnState: true };
}

export const useGridState = (gridId, defaultColumnVisibility, { alwaysHidden = NO_COL_IDS, movableColumns = true } = {}) => {
  const { userContext, updatePreference } = useAuth();
  const tabId = useOptionalTabId();
  const timerRef = useRef(null);
  // The durable changes waiting for the debounced write, per grid id.
  // Accumulated, so a sort inside a second of a column toggle does not drop
  // the toggle.
  const pendingWriteRef = useRef({});
  const slot = `${VIEW_SLOT_PREFIX}${gridId}`;

  // The merchant's current position, mirrored out of the grid as it changes.
  //
  // A ref rather than state because a grid can be rebuilt without anything in
  // React re-rendering: the page's state never changed, so nothing here is
  // allowed to depend on a render happening at the right moment. The ref is
  // simply current whenever the next grid asks.
  //
  // Seeded from sessionStorage, so this also survives F5, and re-seeded if the
  // grid id changes under us (Data Health switches issue key while mounted).
  const viewRef = useRef(null);
  const seededForRef = useRef(null);
  // What the next grid to come up should be put back to. Written every render,
  // read from onStateUpdated — which is memoised, so it cannot close over it.
  const pendingRestoreRef = useRef({ state: null, serverSideInitialRowCount: undefined });
  const seedKey = tabId ? `${tabId}::${slot}` : null;
  if (seededForRef.current !== seedKey) {
    seededForRef.current = seedKey;
    viewRef.current = seedKey ? loadTabSlot(tabId, slot) : null;
  }

  // Every hiddenColIds list leaves through this, so the always-hidden columns
  // are in each of them exactly once.
  const withAlwaysHidden = useCallback(
    (hiddenColIds) => [...new Set([...hiddenColIds, ...alwaysHidden])],
    [alwaysHidden],
  );
  // Read by onStateUpdated, which is memoised once and outlives the render
  // that handed it the current list.
  const alwaysHiddenRef = useRef(alwaysHidden);
  alwaysHiddenRef.current = alwaysHidden;

  // What the MERCHANT chose. A capture is a complete visibility picture of the
  // columns the grid had — it lists every hidden column, including ones the
  // columnDefs hide by default — so it is safe to apply wholesale to a new grid
  // once the always-hidden columns are added (see the header).
  const savedState = useMemo(() => {
    const saved = userContext?.user?.ui_preferences?.grid_state?.[gridId];
    const rest = restorableColumnState(saved);
    if (!rest) return null;
    if (rest.columnVisibility) {
      rest.columnVisibility = {
        ...rest.columnVisibility,
        hiddenColIds: withAlwaysHidden(rest.columnVisibility.hiddenColIds),
      };
    }
    // Required by AG Grid when restoring a subset of column state (we only
    // restore columnVisibility / sort). Without it, AG Grid treats the saved
    // state as the complete column model and excludes any column whose colId
    // isn't listed — including columns added since the state was saved.
    return { ...rest, partialColumnState: true };
  }, [userContext?.user?.ui_preferences?.grid_state, gridId, withAlwaysHidden]);

  // The merchant's column layout as this hook last knew it: what the backend
  // holds, or what was queued for it since (the write waits out DEBOUNCE_MS,
  // and a reset inside that wait has to be compared with the drag before it).
  // A ref, because onStateUpdated restores its widths into every new grid and
  // compares against it before saving. Re-read only when the saved copy itself
  // changes, so a render does not undo a queued layout. A grid with fixed
  // columns knows only the widths (see the header).
  const storedLayout = userContext?.user?.ui_preferences?.grid_state?.[gridId]?.columnLayout ?? null;
  let savedLayout = storedLayout;
  if (!movableColumns) savedLayout = storedLayout?.widths ? { widths: storedLayout.widths } : null;
  const savedLayoutKey = JSON.stringify([gridId, savedLayout]);
  const knownLayoutRef = useRef(null);
  const knownLayoutKeyRef = useRef(null);
  if (knownLayoutKeyRef.current !== savedLayoutKey) {
    knownLayoutKeyRef.current = savedLayoutKey;
    knownLayoutRef.current = savedLayout;
  }
  const savedOrder = savedLayout?.order;

  // What the DEVELOPER chose for a merchant who has chosen nothing: an array of
  // colIds to hide, plus the always-hidden ones. This one is NOT safe to apply
  // to a live grid — it is a hand-written list, so it says nothing about the
  // columns a columnDef hides, and applying it would reveal them. It reaches a
  // grid only through `initialState` at construction, which is identical for
  // every instance of a page because it comes from a page-level constant.
  const defaultState = useMemo(() => {
    const hiddenColIds = withAlwaysHidden(defaultColumnVisibility ?? []);
    return hiddenColIds.length
      ? { columnVisibility: { hiddenColIds }, partialColumnState: true }
      : null;
  }, [defaultColumnVisibility, withAlwaysHidden]);

  // Debounce to avoid spamming backend. Changes wait in pendingWriteRef per
  // grid id (Data Health switches id while mounted). Functional updater
  // reads the latest grid_state from AuthContext's ref, not a stale closure
  // — so two grids that persist within the same debounce window don't race.
  const queueDurableWrite = useCallback((columnState) => {
    const pending = pendingWriteRef.current;
    pending[gridId] = { ...pending[gridId], ...columnState };
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const writes = pendingWriteRef.current;
      pendingWriteRef.current = {};
      updatePreference('grid_state', (allGrids) => {
        const next = { ...(allGrids || {}) };
        for (const [id, changes] of Object.entries(writes)) {
          next[id] = { ...(next[id] || {}), ...changes };
        }
        return next;
      });
    }, DEBOUNCE_MS);
  }, [gridId, updatePreference]);
  // Read by the reset listener a grid keeps for its whole life.
  const queueDurableWriteRef = useRef(queueDurableWrite);
  queueDurableWriteRef.current = queueDurableWrite;

  const onStateUpdated = useCallback((params) => {
    // --- a new grid just came up --------------------------------------------
    // `initialState` cannot serve a REBUILT grid: AG Grid React captures the
    // props it builds from in a ref callback with an empty dependency array
    // (node_modules/ag-grid-react/.../index.esm.mjs `setOutermostRef`), so
    // every grid after the first is created from the props of the page's FIRST
    // render — the state as it was before the merchant did anything, so a
    // rebuild would bring back every column hidden since.
    //
    // Every instance announces itself here instead, and gets the current state
    // applied to it. Values come from a ref because this callback outlives the
    // render that made them.
    //
    // Which way that announcement goes depends on who knows more. If something
    // is stored, it is authoritative — the grid is seconds old and knows
    // nothing — so it is applied and the event is over. If nothing is stored,
    // the grid's own state is the only truth there is, and it is captured like
    // any other event. It has to be, because AG Grid batches: a change made in
    // the moments before a grid finishes coming up arrives folded into THIS
    // event, sometimes as an extra source and sometimes only as the state it
    // carries. Restore or capture, never neither.
    const sources = params.sources || [];
    const initialising = sources.includes('gridInitializing');
    if (initialising) {
      const { state, serverSideInitialRowCount } = pendingRestoreRef.current;
      if (state) {
        // The server-side model will not move to a page it has no rows for, and
        // a grid built this instant has no row count until its first block
        // lands. This is the post-creation equivalent of the
        // serverSideInitialRowCount option; the real count arrives with the
        // block and replaces it.
        if (serverSideInitialRowCount) {
          params.api.setRowCount(serverSideInitialRowCount, false);
        }
        // setState REPLACES the whole grid state, not the parts handed to it:
        // called with just a column-visibility fragment it also wipes the sort a
        // columnDef declared, the filter model, the selection. Its second
        // argument is the list of top-level keys to leave alone: everything the
        // live grid state has that this hook does not manage.
        const untouched = Object.keys(params.api.getState())
          .filter((key) => !(key in state));
        params.api.setState(state, untouched);
      }
      // After setState, so the merchant's widths are the last word.
      restoreMerchantLayout(params.api, knownLayoutRef.current);
      const { api } = params;
      onMerchantLayoutReset(api, () => {
        const layout = changedMerchantLayout(api, knownLayoutRef.current);
        if (layout === undefined) return;
        knownLayoutRef.current = layout;
        queueDurableWriteRef.current({ columnLayout: layout });
      });
      if (state) return;
    }

    // --- volatile: where the merchant is, per tab ---------------------------
    // Rewritten wholesale from the state we were just handed, so paging back to
    // the first page or collapsing the last group clears the slot instead of
    // leaving a position the merchant has left.
    //
    // Only on an event that actually moved the merchant, though — or on a grid
    // that came up with nothing stored, whose state is all there is to go on.
    // Every other event (a column hidden, our own restore) carries the grid's
    // current position too, and a grid that has not been restored yet reports
    // page 1: reading position from those would quietly overwrite where the
    // merchant was with where the new grid happens to be sitting.
    const moved = sources.some((s) => POSITION_SOURCES.includes(s));
    if (tabId && (moved || initialising)) {
      const view = capturedViewState(
        params.state,
        params.api.getGridOption('rowModelType'),
      );
      // Nothing to keep and nothing kept: every client-side grid is in this
      // state permanently, and it should not leave a slot behind to say so.
      if (view || viewRef.current) {
        viewRef.current = view;
        saveTabSlot(tabId, slot, view);
      }
    }

    // --- durable: what the merchant configured, per user --------------------
    // Column visibility, sort and the column layout the merchant dragged.
    const tracked = sources.filter(s =>
      s === 'columnVisibility' || s === 'sort'
    );
    const layoutMoved = sources.some((s) => LAYOUT_SOURCES.includes(s));
    if (tracked.length === 0 && !layoutMoved) return;

    const state = params.state;

    // AG Grid v32+ omits state keys whose current value matches the default
    // (e.g. state.columnVisibility is undefined when no columns are hidden).
    // Key off `sources` — which tells us what actually changed — and write
    // an explicit empty default when the grid reports no state. Otherwise
    // the merge below falls back to the previous saved value and the
    // "unhide last hidden column" / "clear sort" cases silently revert.
    const columnState = {};
    if (tracked.includes('columnVisibility')) {
      const hiddenColIds = (state?.columnVisibility?.hiddenColIds ?? [])
        .filter((colId) => !alwaysHiddenRef.current.includes(colId));
      columnState.columnVisibility = { hiddenColIds };
    }
    if (tracked.includes('sort')) {
      columnState.sort = state?.sort ?? { sortModel: [] };
    }
    if (layoutMoved) {
      const layout = changedMerchantLayout(params.api, knownLayoutRef.current);
      if (layout !== undefined) {
        knownLayoutRef.current = layout;
        columnState.columnLayout = layout;
      }
    }
    if (Object.keys(columnState).length === 0) return;

    queueDurableWrite(columnState);
  }, [tabId, slot, queueDurableWrite]);

  // Recomputed every render on purpose — see viewRef.
  const restore = viewRestoreProps(viewRef.current);

  // Construction-time state: a merchant's choices where they exist, the page's
  // defaults where they don't, plus wherever they had got to. Read once, by the
  // first grid this page builds.
  // A merchant who saved no visibility of their own (only a sort, or only a
  // layout) gets the page's default-hidden columns, as one who saved nothing
  // does. Without a page list there is nothing to fill in, and a visibility
  // state made of the always-hidden columns alone would show every column a
  // columnDef hides.
  const pageDefaultsFill = savedState && !savedState.columnVisibility && defaultColumnVisibility?.length;
  const columnState = withColumnOrder(
    pageDefaultsFill ? { ...defaultState, ...savedState } : savedState || defaultState,
    savedOrder,
  );
  const initialState = columnState || restore.state
    ? { ...(columnState || {}), ...(restore.state || {}) }
    : undefined;

  // Re-application state, for every grid built after that one. Deliberately
  // NARROWER than initialState: the merchant's own state only, never the
  // page's defaults — see defaultState.
  const merchantState = withColumnOrder(savedState, savedOrder);
  pendingRestoreRef.current = {
    state: merchantState || restore.state
      ? { ...(merchantState || {}), ...(restore.state || {}) }
      : null,
    serverSideInitialRowCount: restore.serverSideInitialRowCount,
  };

  // Recomputed, but not necessarily NEW: callers memoise on what we return
  // (ItemSetup derives its own initialState from it), and a fresh object every
  // render would defeat every one of those memos. Both values are plain data,
  // so equality is their serialisation; onStateUpdated is new only when the
  // grid id (or the tab) changes, and then the grid must get the new one, or
  // it keeps saving under the old id.
  const signature = JSON.stringify([initialState ?? null, restore.serverSideInitialRowCount ?? null, movableColumns]);
  const cacheRef = useRef({ signature: null, props: null });
  if (cacheRef.current.signature !== signature || cacheRef.current.props.onStateUpdated !== onStateUpdated) {
    cacheRef.current = {
      signature,
      props: {
        initialState,
        onStateUpdated,
        serverSideInitialRowCount: restore.serverSideInitialRowCount,
        ...(movableColumns ? {} : { suppressMovableColumns: true }),
      },
    };
  }
  return cacheRef.current.props;
};
